import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Coins, Check, Sparkles } from "lucide-react";
import api from "@/lib/api";

/** Public tools-pricing block on the landing page — mirrors the "Bijtanken"
 * section of /tools (usage table + credit packs) so visitors see the tool
 * prices before registering. CTA leads to /register. */
export const LandingToolsPricing = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [packs, setPacks] = useState([]);

    useEffect(() => {
        api.get("/public/credit-packs")
            .then((r) => setPacks(r.data.packs || []))
            .catch(() => {});
    }, []);

    if (!packs.length) return null;

    return (
        <section id="tools-pricing" className="relative py-14 lg:py-20 bg-white border-y border-zinc-100" data-testid="landing-tools-pricing">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#E5A93D] font-semibold mb-4">
                        <Coins className="h-3.5 w-3.5" />
                        {t("landing.tools_pricing.eyebrow")}
                    </div>
                    <h2 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none mb-4 text-balance">
                        {t("landing.tools_pricing.title")}
                    </h2>
                    <p className="text-zinc-500 max-w-2xl text-lg mb-8 leading-relaxed">
                        {t("landing.tools_pricing.subtitle")}
                    </p>
                </motion.div>

                <div className="mb-6 bg-[#FAFAFA] border border-zinc-200 rounded-sm px-5 py-4" data-testid="landing-usage-table">
                    <p className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-bold mb-3">{t("tools.usage_title")}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-2 text-sm">
                        {[
                            [t("tools.usage_retouch"), t("tools.usage_credit_one")],
                            [t("tools.usage_upscale"), t("tools.usage_credit_one")],
                            [t("tools.usage_blur"), t("tools.usage_credit_one")],
                            [t("tools.usage_video"), t("tools.usage_credit_video"), true],
                        ].map(([label, cost, hl]) => (
                            <div key={label} className="border-b border-dashed border-zinc-200 pb-2">
                                <div className="text-zinc-600">{label}</div>
                                <div className={`mt-0.5 font-bold tabular-nums ${hl ? "text-[#E5A93D]" : "text-zinc-950"}`}>{cost}</div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {packs.map((p) => {
                        const isMost = p.key === "pro";
                        return (
                            <div
                                key={p.key}
                                data-testid={`landing-credit-pack-${p.key}`}
                                className={`relative bg-white border rounded-sm p-6 transition-all ${
                                    isMost ? "border-[#E5A93D]/40 ring-1 ring-[#E5A93D]/20" : "border-zinc-200 hover:border-zinc-400"
                                }`}
                            >
                                {isMost && (
                                    <span className="absolute -top-2.5 left-5 px-2 py-0.5 text-[10px] uppercase tracking-wider bg-[#E5A93D] text-black font-bold rounded-sm">
                                        ⭐ {p.tagline || "Meest gekozen"}
                                    </span>
                                )}
                                <div className="flex items-baseline justify-between mb-2">
                                    <h3 className="font-display text-2xl font-bold">{p.label}</h3>
                                    <div className="text-right">
                                        <p className="text-3xl font-bold tabular-nums leading-none" style={{ color: isMost ? "#E5A93D" : "#09090B" }}>
                                            €{p.price_eur.toFixed(0)}
                                        </p>
                                        <p className="text-[10px] uppercase tracking-wider text-zinc-500 mt-1">{t("common.excl_vat")}</p>
                                    </div>
                                </div>
                                <p className="text-sm text-zinc-500 mb-1">
                                    <span className="text-zinc-950 font-semibold tabular-nums">{p.credits}</span> {t("common.credits")}
                                </p>
                                <p className="text-xs text-zinc-500 mb-2">
                                    {t("tools.per_credit", { amount: p.per_credit.toFixed(2) })}
                                </p>
                                <ul className="text-xs text-zinc-500 space-y-1.5 mb-6">
                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-600 shrink-0" /> {t("tools.feature_instant")}</li>
                                    <li className="flex items-center gap-2"><Check className="h-3 w-3 text-emerald-600 shrink-0" /> {t("tools.feature_no_expiry")}</li>
                                </ul>
                                <button
                                    type="button"
                                    onClick={() => navigate("/register")}
                                    data-testid={`landing-credit-pack-cta-${p.key}`}
                                    className={`w-full h-11 rounded-sm font-bold inline-flex items-center justify-center gap-2 transition active:scale-[0.98] ${
                                        isMost ? "bg-[#E5A93D] hover:bg-[#C8922C] text-black" : "bg-white border border-zinc-300 text-zinc-950 hover:bg-zinc-100"
                                    }`}
                                >
                                    <Sparkles className="h-4 w-4" />
                                    {t("landing.tools_pricing.cta")}
                                </button>
                            </div>
                        );
                    })}
                </div>
                <p className="mt-5 text-xs text-zinc-500 text-center">{t("landing.tools_pricing.note")}</p>
            </div>
        </section>
    );
};

export default LandingToolsPricing;
